import { Label } from "@radix-ui/react-label"
import { ChevronsUpDown, Loader2, MoreVertical, Plus, RefreshCw, UserPlus } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import type { UseFormReturn } from "react-hook-form"
import { toast } from "sonner"
import { AutocompleteInput } from "@/components/forms/autoCompleteInput"
import { CustomInput } from "@/components/forms/customInput"
import { CustomSelect } from "@/components/forms/customSelect"
import { CustomTextarea } from "@/components/forms/customTextarea"
import { DateTimePicker } from "@/components/forms/dateTimePicker"
import { DurationPicker } from "@/components/forms/durationPicker"
import { IntentionsInput } from "@/components/forms/intentionsInput"
import { MultiSelectDropdown } from "@/components/forms/multiSelectDropdown"
import { RadioSelect } from "@/components/forms/radioSelect"
import { TagsInput } from "@/components/forms/tagsInput"
import { InviteAcceptedIcon, InviteDeclinedIcon } from "@/components/icons"
import { TrashIcon } from "@/components/icons/index"
import {
  type LocalMediaItem,
  type MediaChanges,
  MediaUploadSection,
} from "@/components/media/mediaUploadSection"
import {
  type RitualOrganizer,
  SelectRitualOrganizersModal,
} from "@/components/modals/selectRitualOrganizersModal"
import type { AccordionItem, AccordionRef } from "@/components/ui/accordion"
import { Accordion } from "@/components/ui/accordion"
import { Avatar } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { RITUAL_MEDIA_LIMITS } from "@/constants/media"
import { StripeConnectAccordion } from "@/features/coaching/components/StripeConnectAccordion"
import {
  useConnectStatus,
  useCreateConnectAccount,
  useDashboardLink,
  useDisconnectAccount,
} from "@/features/settings/hooks/useEarnings"
import { useFormSidebarPositioning } from "@/hooks/useFormSidebarPositioning"
import { useTranslation } from "@/language"
import { cn } from "@/lib"
import { getAssetUrl } from "@/utils/assets"
import { useNativeRitualVideos } from "../hooks/useNativeRitualVideos"
import { useSetRitualAudioCover } from "../hooks/useSetRitualAudioCover"
import type { RitualFormData } from "../schemas/ritual.schema"
import { updateRitualVideo } from "../services/ritual.service"
import { RITUAL_VISIBILITY_OPTIONS, RitualTypeEnum, RitualVisibilityEnum } from "../types"
import type { Organizer, SectionValidationResult } from "../utils/accordionValidation"
import {
  validateDetailsSection,
  validateInfoSection,
  validateOrganizersSection,
  validatePrivacySection,
} from "../utils/accordionValidation"
import { getPrivacyConfig } from "../utils/privacyConfig"
import { getRitualSectionTips } from "../utils/sectionTips"
import type { CourseModule } from "./CourseModulesSection"
import { CourseModulesSection } from "./CourseModulesSection"
import { CustomRepeatInput } from "./CustomRepeatInput"

interface SelectOption {
  value: string
  label: string
}

interface RitualOrganizerApiItem {
  id: string
  organiserType: "user" | "community"
  organiserId: string
  status: "PENDING" | "Accepted" | "DECLINED"
  inviteNote: string | null
  invitedAt: string | null
  joinedAt: string | null
  user?: {
    id: string
    username: string | null
    name: string | null
    profilePictureUrl: string | null
    coverImage: string | null
  } | null
  community?: {
    id: string
    name: string | null
    handle: string | null
    coverImage: string | null
  } | null
}

interface RitualFormUser {
  id: string
  email: string
  name?: string
  username?: string
}

interface RitualUploadItem {
  fileType?: string
  filePath?: string
  isCover?: boolean
}

interface RitualFormEditRitual {
  id?: string
  createdBy?: string | null
  canceledAt?: string | null
  uploads?: RitualUploadItem[]
}

export interface RitualFormProps {
  mode: "create" | "edit"
  form: UseFormReturn<RitualFormData>
  ritualTypeOptions: { value: string; label: string }[]
  repeatOptions: SelectOption[]
  participationFeeOptions: SelectOption[]
  tags: Array<{ id?: string; text: string }> | string[]
  selectedIntentions: Array<{ id: string; name: string }>
  privacySettings: {
    comment: string[]
    edit: string[]
    join: string[]
    invite: string[]
    seeParticipantList: string[]
  }
  organizers: Organizer[]
  courseModules: CourseModule[]
  courseVideoAccess?: "ALL" | "SEQUENTIAL"
  onCourseVideoAccessChange?: (value: "ALL" | "SEQUENTIAL") => void
  preservedValues: { meetingLink: string; steps: string }
  mediaChanges: MediaChanges | null
  initialMedia?: LocalMediaItem[]
  user?: RitualFormUser | null
  onTagAdd: (tag: string) => void
  onTagRemove: (tag: string) => void
  onSetPreservedValues: (
    updater: (prev: { meetingLink: string; steps: string }) => {
      meetingLink: string
      steps: string
    },
  ) => void
  onCommentChange: (options: string[]) => void
  onEditChange: (options: string[]) => void
  onJoinChange: (options: string[]) => void
  onInviteChange: (options: string[]) => void
  onSeeParticipantListChange: (options: string[]) => void
  onMediaChange: (changes: MediaChanges) => void
  /** Reports whether a native video upload is currently in flight (or queued),
   *  so the hosting page can guard against navigating away mid-upload. */
  onUploadingChange?: (uploading: boolean) => void
  onCoverImageChange: (hasCover: boolean) => void
  onAddModule: () => void
  onRemoveModule: (id: string) => void
  onReorderModules: (modules: CourseModule[]) => void
  onUpdateModule: (
    id: string,
    fieldOrUpdates: keyof CourseModule | Partial<CourseModule>,
    value?: string | boolean | File,
  ) => void
  onAddOrganizers: (organizers: RitualOrganizer[]) => void
  onRemoveOrganizer: (id: string) => void
  onAddIntention?: (intention: { id: string; name: string }) => void
  onRemoveIntention?: (id: string) => void
  isSubmitting: boolean
  submitError: string | null
  isOrganizerModalOpen: boolean
  setIsOrganizerModalOpen: (open: boolean) => void
  viewMode?: "edit" | "preview"
  onPreview?: () => void
  onBackToEdit?: () => void
  onSaveDraft?: (isDraft: boolean) => void
  /** create mode: true once a draft has been minted (unlocks the later sections). */
  draftExists?: boolean
  /** create mode: mint the ritual as a draft from the Details step. Returns success. */
  onCreateDraft?: () => Promise<boolean>
  onUpdate?: () => Promise<void>
  renderModals?: () => React.ReactNode
  editProps?: {
    ritual?: RitualFormEditRitual
    ritualOrganizers?: RitualOrganizerApiItem[]
    isLoadingOrganizers?: boolean
    hasNextPage?: boolean
    fetchNextPage?: () => void
    organizersToRemove?: string[]
    onMarkOrganizerForRemoval?: (id: string) => void
    onResendOrganizerInvite?: (data: { organizerId: string }) => void
    onWithdrawOrganizerInvite?: (id: string) => void
    isResending?: boolean
    isWithdrawing?: boolean
  }
  renderPreview?: () => React.ReactNode
  onCancelRitual?: () => void
  onNavigate?: (path: string) => void
  ritualId?: string
  /** Create flow only: receives the hook's flushPending so the create page can
   *  upload videos queued before the ritual existed, once it has the new id. */
  registerNativeVideoFlush?: (flush: (newRitualId: string) => Promise<void>) => void
}

export function RitualForm({
  mode,
  form,
  ritualTypeOptions,
  repeatOptions,
  participationFeeOptions,
  tags,
  selectedIntentions,
  privacySettings,
  organizers,
  courseModules,
  courseVideoAccess,
  onCourseVideoAccessChange,
  preservedValues: _preservedValues,
  mediaChanges,
  initialMedia,
  user,
  onTagAdd,
  onTagRemove,
  onSetPreservedValues,
  onCommentChange,
  onEditChange,
  onJoinChange,
  onInviteChange,
  onSeeParticipantListChange,
  onMediaChange,
  onUploadingChange,
  onCoverImageChange,
  onAddModule,
  onRemoveModule,
  onReorderModules,
  onUpdateModule,
  onAddOrganizers,
  onRemoveOrganizer,
  onAddIntention,
  onRemoveIntention,
  isSubmitting,
  submitError,
  isOrganizerModalOpen,
  setIsOrganizerModalOpen,
  viewMode = "edit",
  onPreview,
  onBackToEdit: _onBackToEdit,
  onSaveDraft,
  draftExists = false,
  onCreateDraft,
  onUpdate,
  renderModals,
  editProps,
  renderPreview,
  onCancelRitual,
  onNavigate,
  ritualId = "",
  registerNativeVideoFlush,
}: RitualFormProps) {
  const { t } = useTranslation("rituals")
  const isEdit = mode === "edit"
  const {
    register,
    formState: { errors },
    trigger,
    clearErrors,
    setValue,
    watch,
  } = form

  // Stripe Connect hooks for paid rituals
  const { data: connectStatus, isLoading: connectLoading } = useConnectStatus()
  const createConnect = useCreateConnectAccount()
  const disconnectAccount = useDisconnectAccount()
  const dashboardLink = useDashboardLink()

  const handleStripeConnect = async () => {
    try {
      // Save current form data to localStorage before redirecting
      const currentFormValues = form.getValues()
      localStorage.setItem("ritualFormDraft", JSON.stringify(currentFormValues))

      const baseUrl = window.location.origin
      const currentPath = window.location.pathname
      const result = await createConnect.mutateAsync({
        returnUrl: `${baseUrl}${currentPath}?returnFromStripe=true`,
        refreshUrl: `${baseUrl}${currentPath}?returnFromStripe=true`,
      })
      window.location.href = result.url
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ||
        "Failed to start Stripe onboarding"
      toast.error(message)
    }
  }

  const handleStripeDisconnect = async () => {
    try {
      await disconnectAccount.mutateAsync()
      toast.success("Stripe account disconnected")
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ||
        "Failed to disconnect Stripe account"
      toast.error(message)
    }
  }

  const handleStripeManage = async () => {
    try {
      const result = await dashboardLink.mutateAsync()
      window.open(result.url, "_blank")
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ||
        "Failed to open Stripe dashboard"
      toast.error(message)
    }
  }

  // Handle return from Stripe Connect - restore form data
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const returnFromStripe = urlParams.get("returnFromStripe")

    if (returnFromStripe === "true") {
      const savedFormData = localStorage.getItem("ritualFormDraft")
      if (savedFormData) {
        try {
          const parsedData = JSON.parse(savedFormData) as Partial<RitualFormData>

          Object.entries(parsedData).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              setValue(key as keyof RitualFormData, value)
            }
          })

          localStorage.removeItem("ritualFormDraft")
          window.history.replaceState({}, "", window.location.pathname)
          toast.success("Stripe connected successfully")
        } catch {
          toast.error("Failed to restore form data")
          localStorage.removeItem("ritualFormDraft")
        }
      }
    }
  }, [setValue])

  const {
    videos: nativeVideos,
    isVideoFile,
    uploadVideo,
    deleteVideo,
    setVideoTitle,
    setVideoThumbnail,
    flushPending: flushPendingVideos,
    hasActiveUpload,
  } = useNativeRitualVideos(ritualId)
  const setAudioCover = useSetRitualAudioCover()

  // Track pending native-video title / thumbnail changes so they can be
  // flushed when the user clicks Update (edit flow only).
  const pendingVideoTitleChangesRef = useRef<Map<string, string>>(new Map())
  const pendingVideoThumbnailChangesRef = useRef<Map<string, File>>(new Map())
  // Stable ref to nativeVideos so callbacks can look up ritualVideoId.
  const nativeVideosRef = useRef(nativeVideos)
  nativeVideosRef.current = nativeVideos

  // Update display immediately; queue the change for persistence on Update.
  const handleNativeVideoTitleChange = useCallback(
    (localId: string, title: string) => {
      setVideoTitle(localId, title)
      pendingVideoTitleChangesRef.current.set(localId, title)
    },
    [setVideoTitle],
  )

  const handleNativeVideoThumbnailChange = useCallback(
    (localId: string, file: File) => {
      setVideoThumbnail(localId, file)
      pendingVideoThumbnailChangesRef.current.set(localId, file)
    },
    [setVideoThumbnail],
  )

  // Flush pending native-video changes to the server. Called just before
  // the Update mutation in edit mode.
  const flushPendingVideoChanges = useCallback(async () => {
    if (!ritualId) return
    const promises: Promise<unknown>[] = []
    for (const [localId, title] of pendingVideoTitleChangesRef.current) {
      const video = nativeVideosRef.current.find(v => v.localId === localId)
      if (video?.ritualVideoId) {
        promises.push(updateRitualVideo(ritualId, video.ritualVideoId, { title }).catch(() => {}))
      }
    }
    for (const [localId, file] of pendingVideoThumbnailChangesRef.current) {
      const video = nativeVideosRef.current.find(v => v.localId === localId)
      if (video?.ritualVideoId) {
        // Always include the current title so a thumbnail-only update
        // doesn't risk the server row losing its title.
        promises.push(
          updateRitualVideo(ritualId, video.ritualVideoId, {
            title: video.title || undefined,
            thumbnail: file,
          }).catch(() => {}),
        )
      }
    }
    pendingVideoTitleChangesRef.current.clear()
    pendingVideoThumbnailChangesRef.current.clear()
    await Promise.all(promises)
  }, [ritualId])

  // Hand the create page our flushPending so it can upload videos queued
  // before the ritual existed, once it has the new id.
  useEffect(() => {
    registerNativeVideoFlush?.(flushPendingVideos)
  }, [registerNativeVideoFlush, flushPendingVideos])

  // Surface in-flight native uploads so the page can warn before the user
  // navigates away and loses them. The hook owns the status taxonomy.
  useEffect(() => {
    onUploadingChange?.(hasActiveUpload)
  }, [hasActiveUpload, onUploadingChange])

  const [sectionValidation, setSectionValidation] = useState<
    Record<string, SectionValidationResult>
  >({})
  const [organizersError, setOrganizersError] = useState<string | null>(null)
  const [mediaError, setMediaError] = useState<string | null>(null)
  const [moduleErrors, setModuleErrors] = useState<
    Record<string, { title?: string; description?: string; video?: string }>
  >({})
  const [courseModulesError, setCourseModulesError] = useState<string | null>(null)
  const [isCourseAccessOpen, setIsCourseAccessOpen] = useState(false)
  const [openAccordionId, setOpenAccordionId] = useState<string | null>("info")
  const [tipModalOpen, setTipModalOpen] = useState(false)
  const [tipModalSectionId, setTipModalSectionId] = useState<string | null>(null)
  const [attemptedSections, setAttemptedSections] = useState<Set<string>>(
    isEdit
      ? new Set(["info", "details", "media", "organizers", "privacy", "courseModules"])
      : new Set(["info"]),
  )
  const accordionRef = useRef<AccordionRef>(null)
  const blobUrlsRef = useRef<string[]>([])

  const { sidebarOffset, accordionContainerRef, setAccordionRef } =
    useFormSidebarPositioning(openAccordionId)

  const handleAccordionOpenChange = useCallback(
    (openItems: Set<string>) => {
      const openItem = Array.from(openItems)[0]
      setOpenAccordionId(openItem || null)
      if (openItems.size > 0 && !isEdit) {
        setAttemptedSections(prev => new Set([...prev, ...openItems]))
      }
    },
    [isEdit],
  )

  const titleValue = watch("title")
  const typeValue = watch("type")
  const dateTimeValue = watch("dateTime")
  const repeatValue = watch("repeat")
  const repeatAtValue = watch("repeatAt")
  const repeatAtTypeValue = watch("repeatAtType")
  const durationValue = watch("duration")
  const addressValue = watch("address")
  const meetingLinkValue = watch("meetingLink")
  const stepsValue = watch("steps")
  const participationFeeValue = watch("participationFee")
  const priceValue = watch("price")
  const feeDetailsValue = watch("feeDetails")
  const minParticipantRequirementValue = watch("minParticipantRequirement")
  const minParticipantsValue = watch("minParticipants")
  const registrationDeadlineValue = watch("registrationDeadline")
  const descriptionValue = watch("description")
  const whatToBringValue = watch("whatToBring")
  const whatYoullExperienceValue = watch("whatYoullExperience")
  const intentionValue = watch("intention")
  const websiteOrSocialMediaValue = watch("websiteOrSocialMedia")
  const tagsValue = watch("tags")
  const privacyVisibilityValue = watch("privacySettings.visibility")
  const privacyCommentValue = watch("privacySettings.comment")
  const privacyEditValue = watch("privacySettings.edit")
  const privacyJoinValue = watch("privacySettings.join")

  const validateCourseModules = useCallback(() => {
    if (watch("type") !== RitualTypeEnum.SELF_GUIDED_COURSE && isEdit) {
      return true
    }
    if (courseModules.length === 0) {
      setCourseModulesError(t("createRitual.validation.atLeastOneModule"))
      setModuleErrors({})
      return false
    }
    setCourseModulesError(null)
    const errs: Record<string, { title?: string; description?: string; video?: string }> = {}
    let hasErrors = false
    courseModules.forEach(mod => {
      const modErrs: { title?: string; description?: string; video?: string } = {}
      if (!mod.title || mod.title.trim() === "") {
        modErrs.title = t("createRitual.validation.moduleTitleRequired")
        hasErrors = true
      }
      if (!mod.description || mod.description.trim() === "") {
        modErrs.description = t("createRitual.validation.moduleDescriptionRequired")
        hasErrors = true
      }
      if (!mod.videoFile && !mod.uploadId) {
        modErrs.video = t("createRitual.validation.moduleVideoRequired")
        hasErrors = true
      }
      if (Object.keys(modErrs).length > 0) {
        errs[mod.id] = modErrs
      }
    })
    setModuleErrors(errs)
    return !hasErrors
  }, [courseModules, watch, t, isEdit])

  // biome-ignore lint/correctness/useExhaustiveDependencies: We intentionally watch individual values to trigger validation
  useEffect(() => {
    const currentFormValues = watch()
    const isPersonal = currentFormValues.visibility === RitualVisibilityEnum.PERSONAL
    const items: Array<{ mediaType: string; isCover?: boolean | null }> =
      mediaChanges?.mediaItems ??
      (editProps?.ritual?.uploads || []).map(
        (u: { fileType?: string; isCover?: boolean | null }) => ({
          mediaType: u.fileType?.startsWith("image/")
            ? "IMAGE"
            : u.fileType?.startsWith("video/")
              ? "VIDEO"
              : "AUDIO",
          isCover: u.isCover,
        }),
      )
    const imageCount = items.filter(i => i.mediaType === "IMAGE").length
    const videoCount = items.filter(i => i.mediaType === "VIDEO").length
    const hasCover = items.some(i => i.mediaType === "IMAGE" && i.isCover)
    const mediaComplete = imageCount > 0 && hasCover
    const parts: string[] = []
    if (imageCount > 0) parts.push(`${imageCount} image${imageCount !== 1 ? "s" : ""}`)
    if (videoCount > 0) parts.push(`${videoCount} video${videoCount !== 1 ? "s" : ""}`)

    const validation: Record<string, SectionValidationResult> = {
      info: validateInfoSection(currentFormValues),
      details: validateDetailsSection(currentFormValues),
      media: {
        isComplete: mediaComplete,
        summary: parts.length > 0 ? parts.join(", ") : undefined,
        error: !mediaComplete
          ? imageCount === 0
            ? "At least one image is required"
            : "Please select a cover image"
          : undefined,
      },
      organizers: isPersonal
        ? { isComplete: true }
        : validateOrganizersSection(organizers, editProps?.ritualOrganizers),
      privacy: isPersonal ? { isComplete: true } : validatePrivacySection(currentFormValues),
      ...(typeValue === RitualTypeEnum.SELF_GUIDED_COURSE
        ? {
            courseModules: {
              isComplete:
                courseModules.length > 0 &&
                courseModules.every(m => m.title && m.description && (m.videoFile || m.uploadId)),
              summary:
                courseModules.length > 0
                  ? `${courseModules.length} module${courseModules.length > 1 ? "s" : ""}`
                  : undefined,
            },
          }
        : {}),
    }
    setSectionValidation(validation)
    if (validation.organizers?.isComplete) setOrganizersError(null)
  }, [
    titleValue,
    typeValue,
    dateTimeValue,
    repeatValue,
    repeatAtValue,
    repeatAtTypeValue,
    durationValue,
    addressValue,
    meetingLinkValue,
    stepsValue,
    participationFeeValue,
    priceValue,
    feeDetailsValue,
    minParticipantRequirementValue,
    minParticipantsValue,
    registrationDeadlineValue,
    descriptionValue,
    whatToBringValue,
    whatYoullExperienceValue,
    intentionValue,
    websiteOrSocialMediaValue,
    tagsValue,
    privacyVisibilityValue,
    privacyCommentValue,
    privacyEditValue,
    privacyJoinValue,
    organizers,
    mediaChanges,
    courseModules,
    editProps?.ritual?.uploads,
    editProps?.ritualOrganizers,
    watch,
  ])

  const handleTipClick = useCallback((sectionId: string) => {
    setTipModalSectionId(sectionId)
    setTipModalOpen(true)
  }, [])

  const getAccordionItems = (): AccordionItem[] => {
    const tagsForInput = Array.isArray(tags)
      ? typeof tags[0] === "object"
        ? (tags as Array<{ id?: string; text: string }>).map(t => t.text)
        : (tags as string[])
      : []

    // Create mode: later sections stay locked until a draft is minted (which
    // mints a ritualId so media can upload live). Edit mode is never locked.
    const sectionsLocked = !isEdit && !draftExists

    const baseAccordionItems: AccordionItem[] = [
      {
        id: "info",
        title: t("createRitual.accordion.info"),
        userTitle: watch("title"),
        showNextButton: true,
        isComplete: sectionValidation.info?.isComplete,
        hasError: !!sectionValidation.info?.error,
        error: sectionValidation.info?.error,
        summary: sectionValidation.info?.summary,
        price: sectionValidation.info?.price,
        onInfoClick: () => handleTipClick("info"),
        content: (
          <div className="space-y-6">
            <p className="text-sm mb-7.5 text-body">{t("createRitual.basicInfoDescription")}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6.25">
              <div className="md:col-span-2">
                <CustomInput
                  {...register("title")}
                  id="title"
                  label={t("createRitual.labels.title")}
                  required
                  placeholder={t("createRitual.placeholders.title")}
                  error={errors.title?.message}
                  onClearError={() => clearErrors("title")}
                  className={isEdit ? undefined : "pb-2.5 !h-7.5"}
                />
              </div>

              <div className="md:col-span-2 flex flex-col gap-2.5">
                <Label className={`text-xs leading-5 text-body uppercase${isEdit ? "" : ""}`}>
                  {t("createRitual.labels.type")}
                  <span className="ms-1">*</span>
                  <span className="ms-2 normal-case text-body/60">
                    {t("createRitual.labels.typeSublabel")}
                  </span>
                </Label>
                <div className="flex flex-col md:flex-row md:flex-wrap md:items-center gap-2.5 md:gap-7.5">
                  {RITUAL_VISIBILITY_OPTIONS.map(option => (
                    <label key={option.value} className="flex items-center gap-2.5 cursor-pointer">
                      <div className="relative flex size-6 items-center justify-center">
                        <input
                          type="radio"
                          name="visibility"
                          value={option.value}
                          disabled={isEdit}
                          checked={watch("visibility") === option.value}
                          onChange={e => {
                            const newVisibility = e.target.value as RitualVisibilityEnum
                            setValue("visibility", newVisibility)
                            clearErrors("visibility")
                            if (isEdit) {
                              setValue(
                                "accessType",
                                newVisibility.toUpperCase() as "PUBLIC" | "PRIVATE" | "PERSONAL",
                              )
                            }
                          }}
                          className="peer sr-only"
                        />
                        <span
                          className={`absolute inset-0 rounded-full border-2 ${isEdit ? "border-[#4a4a4a]" : "border-brand-olive"}`}
                        />
                        <span
                          className={`size-3 rounded-full ${isEdit ? "fill-[#4a4a4a] bg-[#4a4a4a] text-[#4a4a4a]" : "fill-brand-olive bg-brand-olive text-brand-olive"} opacity-0 peer-checked:opacity-100 transition-opacity`}
                        />
                      </div>
                      <div className="flex flex-col">
                        <span
                          className={`text-sm font-semibold leading-6.25 ${isEdit ? "text-body cursor-not-allowed" : "text-color-brand-offwhite"}`}
                        >
                          {option.label}
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
                {errors.visibility && (
                  <p className="text-xs text-brand-terracotta-bright">
                    {errors.visibility.message}
                  </p>
                )}
              </div>

              {watch("visibility") !== RitualVisibilityEnum.PERSONAL && (
                <RadioSelect
                  value={watch("type")}
                  onChange={value => {
                    const currentMeetingLink = watch("meetingLink") || ""
                    const currentSteps = watch("steps") || ""
                    setValue("type", value as RitualTypeEnum)
                    clearErrors("type")
                    onSetPreservedValues(() => ({
                      meetingLink: currentMeetingLink,
                      steps: currentSteps,
                    }))
                  }}
                  options={ritualTypeOptions}
                  label={t("createRitual.labels.format")}
                  required
                  error={errors.type?.message}
                  onClearError={() => clearErrors("type")}
                  disabled={isEdit}
                />
              )}

              {typeValue !== "SELF_GUIDED" && typeValue !== "SELF_GUIDED_COURSE" && (
                <DateTimePicker
                  value={watch("dateTime")}
                  onChange={value => {
                    setValue("dateTime", value)
                    clearErrors("dateTime")
                  }}
                  label={t("createRitual.labels.dateTime")}
                  required
                  showTime
                  disablePastDates={!isEdit}
                  error={errors.dateTime?.message}
                  onClearError={() => clearErrors("dateTime")}
                  disabled={isEdit}
                />
              )}

              {typeValue !== "SELF_GUIDED" && typeValue !== "SELF_GUIDED_COURSE" && (
                <RadioSelect
                  value={watch("repeat")}
                  onChange={value => {
                    setValue("repeat", value)
                    clearErrors("repeat")
                  }}
                  options={repeatOptions}
                  label={t("createRitual.labels.repeat")}
                  required
                  error={errors.repeat?.message}
                  onClearError={() => clearErrors("repeat")}
                  disabled={isEdit}
                />
              )}

              <DurationPicker
                value={watch("duration") || ""}
                onChange={value => {
                  setValue("duration", value)
                  clearErrors("duration")
                }}
                label={t("createRitual.labels.duration")}
                required
                error={errors.duration?.message}
                onClearError={() => clearErrors("duration")}
                disabled={isEdit}
              />

              {typeValue !== "SELF_GUIDED" &&
                typeValue !== "SELF_GUIDED_COURSE" &&
                watch("repeat") === "custom" && (
                  <div className="md:col-span-2 space-y-2">
                    <CustomRepeatInput
                      control={form.control}
                      error={errors.repeatAt?.message || errors.repeatAtType?.message}
                      onClearError={() => {
                        clearErrors("repeatAt")
                        clearErrors("repeatAtType")
                      }}
                      disabled={isEdit}
                    />
                    <p className="text-sm text-muted-foreground">{t("repeat.specifyRepeat")}</p>
                  </div>
                )}

              {watch("type") === RitualTypeEnum.IN_PERSON &&
                watch("visibility") !== RitualVisibilityEnum.PERSONAL && (
                  <div className="md:col-span-2">
                    <AutocompleteInput
                      label={t("createRitual.labels.address")}
                      value={watch("address") || ""}
                      onLocationSelect={locationData => {
                        setValue("address", locationData.address)
                        setValue("city", locationData.city || "")
                        setValue("state", locationData.state || "")
                        setValue("country", locationData.country || "")
                        setValue("lat", locationData.lat)
                        setValue("long", locationData.lng)
                        clearErrors("address")
                      }}
                      onInputChange={value => {
                        setValue("address", value)
                      }}
                      placeholder={
                        isEdit
                          ? t("createRitual.placeholders.enterLocation")
                          : t("createRitual.placeholders.location")
                      }
                      error={errors.address?.message}
                      onClearError={() => clearErrors("address")}
                    />
                  </div>
                )}

              {watch("type") === RitualTypeEnum.VIRTUAL_COURSE && (
                <div className="md:col-span-2">
                  <CustomInput
                    {...register("meetingLink", {
                      onChange: e => {
                        onSetPreservedValues(prev => ({ ...prev, meetingLink: e.target.value }))
                      },
                    })}
                    id="meetingLink"
                    label={t("createRitual.labels.meetingLink")}
                    required
                    placeholder="https://meet.google.com/xxx or https://zoom.us/j/xxx"
                    error={errors.meetingLink?.message}
                    onClearError={() => clearErrors("meetingLink")}
                  />
                </div>
              )}

              {watch("visibility") !== RitualVisibilityEnum.PERSONAL && (
                <>
                  <CustomSelect
                    value={watch("participationFee")}
                    onChange={e => {
                      setValue(
                        "participationFee",
                        e.target.value as "Free" | "Paid" | "Donation-based",
                      )
                      clearErrors("participationFee")
                    }}
                    options={participationFeeOptions}
                    label={t("createRitual.labels.participationFee")}
                    required
                    error={errors.participationFee?.message}
                    onClearError={() => clearErrors("participationFee")}
                  />

                  {watch("participationFee") === "Paid" && (
                    <>
                      <CustomInput
                        {...register("price", {
                          onChange: e => {
                            const value = e.target.value
                            if (value && parseFloat(value) < 0) {
                              setValue("price", "0")
                            }
                          },
                        })}
                        id="price"
                        label={t("createRitual.labels.price")}
                        type="number"
                        min="0"
                        step="1"
                        prefix="$"
                        hideSpinButtons
                        required
                        placeholder="10"
                        error={errors.price?.message}
                        onClearError={() => clearErrors("price")}
                        disabled={isEdit}
                        className={isEdit ? undefined : "pb-2.5 !h-7.5"}
                      />
                      <CustomTextarea
                        {...register("feeDetails")}
                        id="feeDetails"
                        label={t("createRitual.labels.feeDetails")}
                        required
                        placeholder={
                          isEdit
                            ? t("createRitual.placeholders.feeDetailsAlt")
                            : t("createRitual.placeholders.feeDetails")
                        }
                        error={errors.feeDetails?.message}
                        onClearError={() => clearErrors("feeDetails")}
                        disabled={isEdit}
                      />
                    </>
                  )}
                </>
              )}

              {watch("visibility") !== RitualVisibilityEnum.PERSONAL &&
                watch("visibility") !== RitualVisibilityEnum.PRIVATE &&
                watch("type") !== RitualTypeEnum.SELF_GUIDED &&
                watch("type") !== RitualTypeEnum.SELF_GUIDED_COURSE && (
                  <>
                    <div className="flex items-center gap-2.5 md:col-span-2">
                      <Checkbox
                        id="minParticipantRequirement"
                        checked={watch("minParticipantRequirement")}
                        onCheckedChange={checked =>
                          setValue("minParticipantRequirement", checked === true)
                        }
                        className="bg-transparent w-6 h-6"
                      />
                      <label
                        htmlFor="minParticipantRequirement"
                        className="text-sm font-semibold leading-6.25 cursor-pointer select-none"
                      >
                        Activate minimum participant requirement
                      </label>
                    </div>
                    {watch("minParticipantRequirement") && (
                      <CustomInput
                        {...register("minParticipants")}
                        id="minParticipants"
                        label={t("createRitual.labels.minParticipants")}
                        required
                        type="number"
                        min="1"
                        step="1"
                        placeholder={t("createRitual.placeholders.minParticipants")}
                        error={errors.minParticipants?.message}
                        onClearError={() => clearErrors("minParticipants")}
                        className="pb-2.5 !h-7.5"
                      />
                    )}
                    {watch("minParticipantRequirement") && (
                      <DateTimePicker
                        value={watch("registrationDeadline")}
                        onChange={value => {
                          setValue("registrationDeadline", value)
                          clearErrors("registrationDeadline")
                        }}
                        label={t("createRitual.labels.registrationDeadline")}
                        required
                        showTime={false}
                        disablePastDates
                        error={errors.registrationDeadline?.message}
                        onClearError={() => clearErrors("registrationDeadline")}
                      />
                    )}
                  </>
                )}
            </div>
          </div>
        ),
      },

      {
        id: "details",
        title: t("createRitual.accordion.details"),
        showNextButton: true,
        nextButtonLabel: !isEdit && !draftExists ? "Save Draft & Next" : undefined,
        isComplete: sectionValidation.details?.isComplete,
        hasError: !!sectionValidation.details?.error,
        error: sectionValidation.details?.error,
        summary: sectionValidation.details?.summary,
        onInfoClick: () => handleTipClick("details"),
        content: (
          <div className="space-y-6.25">
            <p className="text-sm mb-7.5 text-body">{t("createRitual.detailsDescription")}</p>
            <CustomTextarea
              {...register("description")}
              id="description"
              label={t("createRitual.labels.description")}
              required
              rows={-1}
              placeholder={
                isEdit
                  ? t("createRitual.placeholders.describeWhatHappens")
                  : t("createRitual.placeholders.description")
              }
              error={errors.description?.message}
              onClearError={() => clearErrors("description")}
            />

            {watch("visibility") !== RitualVisibilityEnum.PERSONAL && (
              <>
                <CustomTextarea
                  {...register("whatToBring")}
                  id="whatToBring"
                  label={t("createRitual.labels.whatToBring")}
                  placeholder={
                    isEdit
                      ? t("createRitual.placeholders.whatToBringAlt")
                      : t("createRitual.placeholders.whatToBring")
                  }
                  error={errors.whatToBring?.message}
                  onClearError={() => clearErrors("whatToBring")}
                />
                <CustomTextarea
                  {...register("whatYoullExperience")}
                  id="whatYoullExperience"
                  label={t("createRitual.labels.whatYoullExperience")}
                  placeholder={
                    isEdit
                      ? t("createRitual.placeholders.whatYouExperienceAlt")
                      : t("createRitual.placeholders.whatYouExperience")
                  }
                  error={errors.whatYoullExperience?.message}
                  onClearError={() => clearErrors("whatYoullExperience")}
                />
              </>
            )}

            <IntentionsInput
              id="intentions"
              label={t("createRitual.labels.intention")}
              placeholder="Search intentions..."
              selectedIntentions={selectedIntentions}
              onAdd={intention => {
                const currentIds = watch("intentionIds") || []
                if (!currentIds.includes(intention.id)) {
                  setValue("intentionIds", [...currentIds, intention.id])
                }
                onAddIntention?.(intention)
              }}
              onRemove={id => {
                setValue(
                  "intentionIds",
                  (watch("intentionIds") || []).filter(i => i !== id),
                )
                onRemoveIntention?.(id)
              }}
              error={errors.intentionIds?.message}
              onClearError={() => clearErrors("intentionIds")}
            />

            {watch("type") === RitualTypeEnum.SELF_GUIDED && (
              <CustomTextarea
                {...register("steps", {
                  onChange: e => {
                    onSetPreservedValues(prev => ({ ...prev, steps: e.target.value }))
                  },
                })}
                id="steps"
                label={t("createRitual.labels.stepsToPerform")}
                required
                rows={4}
                placeholder={t("createRitual.placeholders.steps")}
                error={errors.steps?.message}
                onClearError={() => clearErrors("steps")}
              />
            )}

            {watch("visibility") !== RitualVisibilityEnum.PERSONAL && (
              <CustomInput
                {...register("websiteOrSocialMedia")}
                id="websiteOrSocialMedia"
                label={t("createRitual.labels.websiteSocial")}
                placeholder="https://your-website.com"
                error={errors.websiteOrSocialMedia?.message}
                onClearError={() => clearErrors("websiteOrSocialMedia")}
              />
            )}

            <TagsInput
              id="ritual-tags"
              label={t("createRitual.labels.tags")}
              tags={tagsForInput}
              onAddTag={onTagAdd}
              onRemoveTag={onTagRemove}
              placeholder={
                isEdit
                  ? t("createRitual.placeholders.tagsAlt")
                  : t("createRitual.placeholders.tags")
              }
              error={errors.tags?.message}
              onClearError={() => clearErrors("tags")}
              enableSearch={true}
              required
            />
          </div>
        ),
      },

      {
        id: "organizers",
        title: t("createRitual.accordion.organizers"),
        showNextButton: !isEdit,
        disabled: sectionsLocked,
        lockedHint: sectionsLocked ? "Save a draft to unlock" : undefined,
        isComplete: sectionValidation.organizers?.isComplete,
        hasError: !!sectionValidation.organizers?.error,
        error: organizersError ?? undefined,
        summary: sectionValidation.organizers?.summary,
        userPreviews: sectionValidation.organizers?.userPreviews,
        showViewMore: isEdit
          ? (editProps?.ritualOrganizers?.length ?? 0) > 3
          : organizers.length > 3,
        onInfoClick: () => handleTipClick("organizers"),
        content: (
          <div className={isEdit ? "space-y-6" : "space-y-5"}>
            <p className="text-body">{t("createRitual.organizersDescription")}</p>
            {organizersError && (
              <p className="text-xs text-brand-terracotta-bright">{organizersError}</p>
            )}

            {isEdit && editProps && (
              <>
                {editProps.isLoadingOrganizers && (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                )}
                {!editProps.isLoadingOrganizers &&
                  ((editProps.ritualOrganizers?.length ?? 0) > 0 || organizers.length > 0) && (
                    <div className="space-y-4">
                      {(editProps.ritualOrganizers || [])
                        .filter(org => !(editProps.organizersToRemove || []).includes(org.id))
                        .sort((a, b) => {
                          const ritual = editProps.ritual
                          const aIsOwner = ritual?.createdBy
                            ? a.organiserId === ritual.createdBy
                            : false
                          const bIsOwner = ritual?.createdBy
                            ? b.organiserId === ritual.createdBy
                            : false
                          if (aIsOwner && !bIsOwner) return -1
                          if (!aIsOwner && bIsOwner) return 1
                          return 0
                        })
                        .map(org => {
                          const isUser = org.organiserType === "user"
                          const avatar = isUser
                            ? org.user?.coverImage || org.user?.profilePictureUrl
                            : org.community?.coverImage
                          const name = isUser
                            ? org.user?.name || t("createRitual.fallback.noName")
                            : org.community?.name || t("createRitual.fallback.community")
                          const identifier = isUser ? org.user?.username : org.community?.handle
                          const isOwner = editProps.ritual?.createdBy
                            ? org.organiserId === editProps.ritual.createdBy
                            : false
                          const getStatusLabel = () => {
                            if (isOwner) return "Owner"
                            switch (org.status) {
                              case "PENDING":
                                return "Invited"
                              case "Accepted":
                                return "Accepted"
                              case "DECLINED":
                                return "Declined"
                              default:
                                return org.status
                            }
                          }
                          const statusLabel = getStatusLabel()
                          return (
                            <div key={org.id} className="flex items-center gap-4">
                              <div className="w-12 h-12 rounded-full bg-themed flex-shrink-0 flex items-center justify-center overflow-hidden">
                                {avatar ? (
                                  <img
                                    src={avatar}
                                    alt={name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <span className="font-semibold">
                                    {name?.[0]?.toUpperCase() || "?"}
                                  </span>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="font-semibold truncate">
                                    {name || t("createRitual.fallback.noName")}
                                  </p>
                                </div>
                                <p className="text-sm truncate">
                                  {identifier ? `@${identifier}` : identifier}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                {!isOwner && (
                                  <span className="flex items-center gap-1.5 text-[14px] text-white">
                                    {org.status === "PENDING" ? (
                                      <img
                                        src={`${import.meta.env.VITE_ASSET_BASE_URL}add_person.svg`}
                                        alt=""
                                        className="w-6.5 h-6.5"
                                      />
                                    ) : org.status === "Accepted" ? (
                                      <InviteAcceptedIcon size={22} />
                                    ) : org.status === "DECLINED" ? (
                                      <InviteDeclinedIcon size={22} color="#C86757" />
                                    ) : null}
                                    {statusLabel}
                                  </span>
                                )}
                                {!isOwner && (
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 flex-shrink-0"
                                      >
                                        <MoreVertical size={18} />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      {org.status !== "PENDING" && (
                                        <DropdownMenuItem
                                          onClick={() =>
                                            editProps.onMarkOrganizerForRemoval?.(org.id)
                                          }
                                          className="text-[#C86757] cursor-pointer"
                                        >
                                          <TrashIcon size={16} className="mr-2" />
                                          Remove
                                        </DropdownMenuItem>
                                      )}
                                      {org.status === "DECLINED" && (
                                        <DropdownMenuItem
                                          onClick={() =>
                                            editProps.onResendOrganizerInvite?.({
                                              organizerId: org.id,
                                            })
                                          }
                                          disabled={editProps.isResending}
                                          className="cursor-pointer"
                                        >
                                          <RefreshCw size={16} className="mr-2" />
                                          Resend
                                        </DropdownMenuItem>
                                      )}
                                      {org.status === "PENDING" && (
                                        <>
                                          <DropdownMenuItem
                                            onClick={() =>
                                              editProps.onResendOrganizerInvite?.({
                                                organizerId: org.id,
                                              })
                                            }
                                            disabled={editProps.isResending}
                                            className="cursor-pointer"
                                          >
                                            <RefreshCw size={16} className="mr-2" />
                                            Resend
                                          </DropdownMenuItem>
                                          <DropdownMenuItem
                                            onClick={() =>
                                              editProps.onWithdrawOrganizerInvite?.(org.id)
                                            }
                                            disabled={editProps.isWithdrawing}
                                            className="text-[#C86757] focus:text-[#C86757] cursor-pointer"
                                          >
                                            <TrashIcon size={16} className="mr-2" />
                                            Withdraw
                                          </DropdownMenuItem>
                                        </>
                                      )}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                )}
                              </div>
                            </div>
                          )
                        })}
                      {organizers.map(org => {
                        const alreadyExists = (editProps.ritualOrganizers || []).some(
                          o => o.id === org.id,
                        )
                        if (alreadyExists) return null
                        return (
                          <div key={org.id} className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-themed flex-shrink-0 flex items-center justify-center overflow-hidden">
                              {org.avatar ? (
                                <img
                                  src={org.avatar}
                                  alt={org.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <Avatar
                                  fallback={org.name || "?"}
                                  size="md"
                                  className="w-full h-full"
                                />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-semibold truncate">
                                  {org.name || t("createRitual.fallback.noName")}
                                </p>
                              </div>
                              <p className="text-sm truncate">
                                {org.identifier ? `@${org.identifier}` : org.identifier}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              {org.id !== user?.id && (
                                <span className="flex items-center gap-1.5 text-[14px] text-white">
                                  <img
                                    src={`${import.meta.env.VITE_ASSET_BASE_URL}add_person.svg`}
                                    alt=""
                                    className="w-6 h-6"
                                  />
                                  Invited
                                </span>
                              )}
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 flex-shrink-0"
                                  >
                                    <MoreVertical size={18} />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={() => {
                                      const updated = organizers.filter(o => o.id !== org.id)
                                      setValue("organizers", updated)
                                      trigger("organizers")
                                    }}
                                    className="text-[#C86757] focus:text-[#C86757] cursor-pointer"
                                  >
                                    <TrashIcon size={16} />
                                    Remove
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                        )
                      })}
                      {editProps.hasNextPage && (
                        <Button
                          variant="ghost"
                          onClick={() => editProps.fetchNextPage?.()}
                          className="w-full text-sm text-stone"
                        >
                          Load more organizers
                        </Button>
                      )}
                    </div>
                  )}
                {!editProps.isLoadingOrganizers &&
                  (editProps.ritualOrganizers?.length ?? 0) === 0 &&
                  organizers.length === 0 && (
                    <p className="text-sm text-stone">No organizers yet</p>
                  )}
              </>
            )}

            {!isEdit && organizers.length > 0 && (
              <div className="md:space-y-5 py-2.5">
                {organizers.map(org => (
                  <div
                    key={org.id}
                    className="flex items-center gap-4 first:border-t border-b border-[#2A2A2A] md:border-0 md:first:border-0 py-2.5 px-5 md:p-0"
                  >
                    <div className="w-12.5 h-12.5 rounded-full bg-themed flex-shrink-0 flex items-center justify-center overflow-hidden">
                      {org.avatar ? (
                        <img
                          src={org.avatar}
                          alt={org.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Avatar fallback={org.name || "?"} size="md" className="w-full h-full" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold truncate">
                          {org.name || t("createRitual.fallback.noName")}
                        </p>
                      </div>
                      <p className="text-sm truncate text-[#FFFFFF66]">
                        {org.identifier ? `@${org.identifier}` : ""}
                      </p>
                    </div>
                    {org.id !== user?.id && (
                      <span className="flex items-center gap-1.5 text-[14px] text-white">
                        <img src={getAssetUrl("add_person.svg")} alt="" className="w-6 h-6" />
                        Invited
                      </span>
                    )}
                    {org.id !== user?.id ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                            <MoreVertical size={18} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => onRemoveOrganizer(org.id)}
                            className="text-[#C86757]"
                          >
                            <TrashIcon size={16} />
                            {t("createRitual.buttons.remove")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : (
                      <div className="w-8" />
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-4">
              <Button
                variant="ghost"
                onClick={() => setIsOrganizerModalOpen(true)}
                className={
                  isEdit
                    ? "flex items-center gap-2 px-3 h-8 rounded-lg text-sm font-medium text-primary"
                    : "flex items-center gap-1.25 px-0 h-fit rounded-lg text-sm font-semibold text-primary"
                }
              >
                {isEdit ? <UserPlus size={18} /> : <Plus size={20} />}
                {t("createRitual.buttons.inviteOrganizer")}
              </Button>
            </div>
          </div>
        ),
      },

      {
        id: "media",
        title: t("createRitual.accordion.uploadMedia"),
        showNextButton: true,
        disabled: sectionsLocked,
        lockedHint: sectionsLocked ? "Save a draft to unlock" : undefined,
        isComplete: sectionValidation.media?.isComplete,
        hasError: !!sectionValidation.media?.error || mediaError !== null,
        error: sectionValidation.media?.error ?? mediaError ?? undefined,
        summary: sectionValidation.media?.summary,
        onInfoClick: () => handleTipClick("media"),
        content: (
          <div className="space-y-4">
            <p className="text-sm text-body mb-5">{t("createRitual.mediaDescription")}</p>
            <MediaUploadSection
              initialMedia={initialMedia}
              onMediaChange={onMediaChange}
              onCoverImageChange={onCoverImageChange}
              showAudioSection={true}
              maxFiles={RITUAL_MEDIA_LIMITS.IMAGES.MAX_FILES}
              maxImageFileSize={RITUAL_MEDIA_LIMITS.IMAGES.MAX_FILE_SIZE}
              maxVideoFileSize={RITUAL_MEDIA_LIMITS.VIDEOS.MAX_FILE_SIZE}
              maxAudioFileSize={RITUAL_MEDIA_LIMITS.AUDIO.MAX_FILE_SIZE}
              allowedImageTypes={[...RITUAL_MEDIA_LIMITS.IMAGES.ALLOWED_TYPES]}
              allowedVideoTypes={[...RITUAL_MEDIA_LIMITS.VIDEOS.ALLOWED_TYPES]}
              allowedAudioTypes={[...RITUAL_MEDIA_LIMITS.AUDIO.ALLOWED_TYPES]}
              enableAiGeneration={true}
              aiEntityType="ritual"
              aiEntityContext={{
                name: titleValue,
                description: descriptionValue,
                type: typeValue,
              }}
              nativeVideos={nativeVideos}
              isVideoFile={isVideoFile}
              onUploadVideo={uploadVideo}
              onDeleteVideo={deleteVideo}
              onNativeVideoTitleChange={handleNativeVideoTitleChange}
              onNativeVideoThumbnailChange={handleNativeVideoThumbnailChange}
              onUploadAudioCover={setAudioCover.mutateAsync}
              ritualId={ritualId}
            />
            {mediaError && (
              <p className="text-xs text-brand-terracotta-bright mt-1">{mediaError}</p>
            )}
          </div>
        ),
      },

      {
        id: "privacy",
        title: t("createRitual.accordion.privacy"),
        showNextButton: !isEdit,
        disabled: sectionsLocked,
        lockedHint: sectionsLocked ? "Save a draft to unlock" : undefined,
        isComplete: sectionValidation.privacy?.isComplete,
        hasError: !!sectionValidation.privacy?.error,
        error: sectionValidation.privacy?.error,
        summary: sectionValidation.privacy?.summary,
        onInfoClick: () => handleTipClick("privacy"),
        content: (() => {
          const visibility = watch("visibility") as RitualVisibilityEnum
          const privacyConfig = getPrivacyConfig(visibility)
          return (
            <div className="space-y-3.75 md:space-y-6">
              <p className={isEdit ? "text-sm mb-7 text-body" : "text-body"}>
                {t("createRitual.privacyDescription")}
              </p>
              <div className="space-y-3.75 md:space-y-6">
                <MultiSelectDropdown
                  label={t("createRitual.privacySettings.whoCanJoin")}
                  options={privacyConfig.join.options}
                  selectedOptions={privacySettings.join}
                  onChange={onJoinChange}
                  disabledOptions={privacyConfig.join.disabledOptions}
                  autoSelectedOptions={privacyConfig.join.autoSelected}
                  groupExclusion={privacyConfig.join.groupExclusion}
                  selectAllWith={privacyConfig.join.selectAllWith}
                  autoSelectParent={privacyConfig.join.autoSelectParent}
                  collapseDisplayOptions={privacyConfig.join.collapseDisplayOptions}
                />
                {isEdit && errors.privacySettings?.join && (
                  <p className="text-xs text-brand-terracotta-bright">
                    {errors.privacySettings.join.message}
                  </p>
                )}

                <MultiSelectDropdown
                  label={t("createRitual.privacySettings.whoCanInvite")}
                  options={privacyConfig.invite.options}
                  selectedOptions={privacySettings.invite}
                  onChange={onInviteChange}
                  disabledOptions={privacyConfig.invite.disabledOptions}
                  autoSelectedOptions={privacyConfig.invite.autoSelected}
                />
                {isEdit && errors.privacySettings?.invite && (
                  <p className="text-xs text-brand-terracotta-bright">
                    {errors.privacySettings.invite.message}
                  </p>
                )}

                <MultiSelectDropdown
                  label={t("createRitual.privacySettings.whoCanComment")}
                  options={privacyConfig.comment.options}
                  selectedOptions={privacySettings.comment}
                  onChange={onCommentChange}
                  disabledOptions={privacyConfig.comment.disabledOptions}
                  autoSelectedOptions={privacyConfig.comment.autoSelected}
                  selectAllWith={privacyConfig.comment.selectAllWith}
                  autoSelectParent={privacyConfig.comment.autoSelectParent}
                  collapseDisplayOptions={privacyConfig.comment.collapseDisplayOptions}
                />
                {isEdit && errors.privacySettings?.comment && (
                  <p className="text-xs text-brand-terracotta-bright">
                    {errors.privacySettings.comment.message}
                  </p>
                )}

                <MultiSelectDropdown
                  label={t("createRitual.privacySettings.whoCanEdit")}
                  options={privacyConfig.edit.options}
                  selectedOptions={privacySettings.edit}
                  onChange={onEditChange}
                  disabledOptions={privacyConfig.edit.disabledOptions}
                  autoSelectedOptions={privacyConfig.edit.autoSelected}
                />
                {isEdit && errors.privacySettings?.edit && (
                  <p className="text-xs text-brand-terracotta-bright">
                    {errors.privacySettings.edit.message}
                  </p>
                )}

                <MultiSelectDropdown
                  label={t("createRitual.privacySettings.whoCanSeeParticipantList")}
                  options={privacyConfig.seeParticipantList.options}
                  selectedOptions={privacySettings.seeParticipantList}
                  onChange={onSeeParticipantListChange}
                  disabledOptions={privacyConfig.seeParticipantList.disabledOptions}
                  autoSelectedOptions={privacyConfig.seeParticipantList.autoSelected}
                  selectAllWith={privacyConfig.seeParticipantList.selectAllWith}
                  autoSelectParent={privacyConfig.seeParticipantList.autoSelectParent}
                  collapseDisplayOptions={privacyConfig.seeParticipantList.collapseDisplayOptions}
                />
                {isEdit && errors.privacySettings?.seeParticipantList && (
                  <p className="text-xs text-brand-terracotta-bright">
                    {errors.privacySettings.seeParticipantList.message}
                  </p>
                )}
              </div>
            </div>
          )
        })(),
      },
      ...(watch("participationFee") === "Paid" || watch("participationFee") === "Donation-based"
        ? [
            {
              id: "stripe-connect",
              title: "Connect Stripe",
              showNextButton: false,
              isComplete: connectStatus?.connected ?? false,
              hasError: false,
              summary: connectStatus?.connected ? "Connected" : "Not connected",
              content: (
                <StripeConnectAccordion
                  connectStatus={connectStatus}
                  isLoading={connectLoading}
                  isConnecting={createConnect.isPending}
                  isDisconnecting={disconnectAccount.isPending}
                  isManaging={dashboardLink.isPending}
                  onConnect={handleStripeConnect}
                  onDisconnect={handleStripeDisconnect}
                  onManage={handleStripeManage}
                />
              ),
            },
          ]
        : []),
    ]

    const isPersonal = watch("visibility") === RitualVisibilityEnum.PERSONAL
    const items = baseAccordionItems.filter(item => {
      if (isPersonal) return item.id !== "organizers" && item.id !== "privacy"
      return true
    })

    if (watch("type") === RitualTypeEnum.SELF_GUIDED_COURSE) {
      const mediaIndex = items.findIndex(item => item.id === "media")
      const courseModulesItem: AccordionItem = {
        id: "courseModules",
        title: t("createRitual.accordion.courseModules"),
        showNextButton: true,
        disabled: sectionsLocked,
        lockedHint: sectionsLocked ? "Save a draft to unlock" : undefined,
        isComplete:
          courseModules.length > 0 &&
          courseModules.every(m => m.title && m.description && (m.videoFile || m.uploadId)),
        hasError: courseModulesError !== null || Object.keys(moduleErrors).length > 0,
        error: courseModulesError ?? undefined,
        summary:
          courseModules.length > 0
            ? `${courseModules.length} module${courseModules.length > 1 ? "s" : ""}`
            : undefined,
        onInfoClick: () => handleTipClick("courseModules"),
        content: (
          <div className="space-y-4">
            <p className="text-body">{t("createRitual.courseModulesDescription")}</p>
            <CourseModulesSection
              modules={courseModules}
              onAddModule={onAddModule}
              onRemoveModule={onRemoveModule}
              onReorderModules={onReorderModules}
              onUpdateModule={onUpdateModule}
              moduleErrors={moduleErrors}
              sectionError={courseModulesError}
            />

            {/* Course Video Access Control */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1.25 pt-5 mt-4 border-t border-[#2A2A2A]">
              <span className="text-sm text-primary font-semibold flex-1">
                Control how participants access videos
              </span>
              <Popover open={isCourseAccessOpen} onOpenChange={setIsCourseAccessOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="md:min-w-[200px] max-w-[70vw] w-fit md:max-w-none min-w-0 rounded-full md:rounded-none text-sm font-semibold text-primary text-left focus:outline-none bg-[#F2F0EB1A] md:bg-transparent transition-all duration-200 flex items-center md:justify-end gap-1.25 px-3.75 py-2.5 md:px-0 md:py-0"
                  >
                    <span className="truncate min-w-0">
                      {courseVideoAccess === "SEQUENTIAL"
                        ? "Unlock next video after completion"
                        : "All videos available"}
                    </span>
                    <ChevronsUpDown
                      className={cn(
                        "transition-transform duration-200",
                        isCourseAccessOpen && "rotate-180",
                      )}
                      size={16}
                      strokeWidth={2}
                    />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="end"
                  sideOffset={10}
                  className="min-w-[280px] bg-popover border border-border rounded-[20px] p-[20px]"
                >
                  <div className="flex flex-col gap-[10px]">
                    <label className="flex items-center gap-[10px] cursor-pointer">
                      <Checkbox
                        checked={courseVideoAccess === "ALL"}
                        onCheckedChange={() => {
                          onCourseVideoAccessChange?.("ALL")
                          setIsCourseAccessOpen(false)
                        }}
                        className="size-[24px] rounded-[6px]"
                      />
                      <span
                        className={cn(
                          "text-[14px]",
                          courseVideoAccess === "ALL" ? "font-semibold" : "font-normal",
                        )}
                      >
                        All videos available
                      </span>
                    </label>
                    <label className="flex items-center gap-[10px] cursor-pointer">
                      <Checkbox
                        checked={courseVideoAccess === "SEQUENTIAL"}
                        onCheckedChange={() => {
                          onCourseVideoAccessChange?.("SEQUENTIAL")
                          setIsCourseAccessOpen(false)
                        }}
                        className="size-[24px] rounded-[6px]"
                      />
                      <span
                        className={cn(
                          "text-[14px]",
                          courseVideoAccess === "SEQUENTIAL" ? "font-semibold" : "font-normal",
                        )}
                      >
                        Unlock next video after completion
                      </span>
                    </label>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        ),
      }
      if (isEdit) {
        items.splice(mediaIndex + 1, 0, courseModulesItem)
      } else {
        items.splice(mediaIndex + 1, 0, courseModulesItem)
      }
    }

    return items
  }

  const accordionItems = getAccordionItems()

  const handleNext = async (currentIndex: number): Promise<boolean> => {
    const currentAccordionId = accordionItems[currentIndex]?.id
    if (!currentAccordionId) return true
    let fieldsToValidate: (keyof RitualFormData)[] = []
    switch (currentAccordionId) {
      case "info":
        fieldsToValidate = ["title", "type", "dateTime", "repeat", "duration", "participationFee"]
        if (watch("participationFee") === "Paid") fieldsToValidate.push("price", "feeDetails")
        if (watch("type") === RitualTypeEnum.IN_PERSON) fieldsToValidate.push("address")
        if (watch("type") === RitualTypeEnum.VIRTUAL_COURSE) fieldsToValidate.push("meetingLink")
        if (watch("minParticipantRequirement") === true)
          fieldsToValidate.push("minParticipants", "registrationDeadline")
        break
      case "details": {
        fieldsToValidate = ["description", "tags"]
        if (watch("type") === RitualTypeEnum.SELF_GUIDED) fieldsToValidate.push("steps")
        // Create mode, no draft yet: this section's "Save Draft & Next" mints the
        // ritual as a draft (which unlocks the later sections + lets media upload
        // live). Validate Info + Details first, then create.
        if (!isEdit && !draftExists && onCreateDraft) {
          const infoFields: (keyof RitualFormData)[] = [
            "title",
            "type",
            "dateTime",
            "repeat",
            "duration",
            "participationFee",
          ]
          if (watch("participationFee") === "Paid") infoFields.push("price", "feeDetails")
          if (watch("type") === RitualTypeEnum.IN_PERSON) infoFields.push("address")
          if (watch("type") === RitualTypeEnum.VIRTUAL_COURSE) infoFields.push("meetingLink")
          if (watch("minParticipantRequirement") === true)
            infoFields.push("minParticipants", "registrationDeadline")
          const valid = await trigger([...infoFields, ...fieldsToValidate])
          if (!valid) return false
          return await onCreateDraft()
        }
        break
      }
      case "courseModules":
        return validateCourseModules()
      case "media": {
        const items: Array<{ mediaType: string; isCover?: boolean | null }> =
          mediaChanges?.mediaItems ??
          (editProps?.ritual?.uploads || []).map(
            (u: { fileType?: string; isCover?: boolean | null }) => ({
              mediaType: u.fileType?.startsWith("image/") ? "IMAGE" : "OTHER",
              isCover: u.isCover,
            }),
          )
        const imageCount = items.filter(i => i.mediaType === "IMAGE").length
        const hasCover = items.some(i => i.mediaType === "IMAGE" && i.isCover)
        if (imageCount === 0) {
          setMediaError(t("createRitual.validation.atLeastOneImage"))
          return false
        }
        if (!hasCover) {
          setMediaError(t("createRitual.validation.selectCoverImage"))
          return false
        }
        setMediaError(null)
        return true
      }
      case "organizers": {
        if (watch("visibility") === RitualVisibilityEnum.PERSONAL) return true
        if (!organizers || organizers.length === 0) {
          setOrganizersError(t("createRitual.validation.atLeastOneOrganizer"))
          return false
        }
        setOrganizersError(null)
        return true
      }
      case "privacy":
        if (watch("visibility") === RitualVisibilityEnum.PERSONAL) return true
        fieldsToValidate = ["privacySettings"]
        break
      default:
        return true
    }
    return await trigger(fieldsToValidate)
  }

  const handleItemClose = (closedItemId: string) => {
    if (closedItemId === "courseModules") {
      setTimeout(() => validateCourseModules(), 0)
    }
  }

  useEffect(() => {
    return () => {
      for (const url of blobUrlsRef.current) URL.revokeObjectURL(url)
    }
  }, [])

  const getMediaPreviewImages = () => {
    if (isEdit && editProps?.ritual) {
      return (editProps.ritual.uploads || [])
        .filter(u => u.fileType?.startsWith("image/"))
        .map(u => ({
          url: u.filePath?.startsWith("http") ? u.filePath : getAssetUrl(u.filePath),
          isCover: u.isCover || false,
        }))
    }
    const items = mediaChanges?.mediaItems ?? []
    const imageItems = items.filter(i => i.mediaType === "IMAGE" && i.file)
    return imageItems.map(img => ({
      url: URL.createObjectURL(img.file as File),
      isCover: img.isCover,
    }))
  }

  const sectionTips = getRitualSectionTips(openAccordionId)

  if (viewMode === "preview" && renderPreview) {
    return <>{renderPreview()}</>
  }

  return (
    <div
      className={
        isEdit ? "min-h-screen px-5 md:px-17.5" : "min-h-screen max-w-360 mx-auto px-5 md:px-17.5"
      }
    >
      <div
        className={isEdit ? "max-w-360 mx-auto pb-4 md:py-10" : "max-w-8xl mx-auto pb-0 md:py-10"}
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 max-w-[850px]" ref={accordionContainerRef}>
            <Accordion
              ref={accordionRef}
              items={accordionItems}
              defaultOpenIndex={0}
              allowMultiple={false}
              onNextClick={handleNext}
              onItemClose={handleItemClose}
              onOpenChange={handleAccordionOpenChange}
              onSectionAttempt={sectionId =>
                setAttemptedSections(prev => new Set([...prev, sectionId]))
              }
              accordionItemRef={setAccordionRef}
              attemptedSections={attemptedSections}
            />
          </div>

          <div className="sticky md:top-4">
            <div
              className="hidden lg:block"
              style={{ height: `${sidebarOffset}px`, transition: "height 250ms ease-in-out" }}
            />

            {openAccordionId === "media" &&
              sectionTips.showPreview &&
              (() => {
                const images = getMediaPreviewImages()
                if (images.length === 0) return null
                return (
                  <div className="my-5 hidden lg:block">
                    <p className="text-[18px] font-semibold leading-6.25 text-body mb-3.75">
                      Card Preview
                    </p>
                    <div className="grid grid-cols-2 gap-2.5">
                      {images.map((img: { url: string; isCover: boolean }, index: number) => (
                        <div
                          key={index}
                          className="relative aspect-video rounded-tl-lg rounded-br-lg overflow-hidden bg-muted"
                        >
                          <img
                            src={img.url}
                            alt={`Image ${index + 1} preview`}
                            className="w-full h-full object-cover"
                          />
                          {img.isCover && (
                            <div className="absolute top-1 left-1 bg-brand-olive text-white text-[10px] px-1.5 py-0.5 rounded font-medium">
                              Cover
                            </div>
                          )}
                          <div className="absolute bottom-0 left-0 p-2 space-y-1">
                            <div className="flex items-center gap-1">
                              <div className="h-1.5 w-12 bg-white/60"></div>
                              <div className="h-1.5 w-5 bg-white/60"></div>
                              <div className="h-1.5 w-12 bg-white/60"></div>
                              <div className="h-1.5 w-13 bg-white/60"></div>
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="h-1 w-12 bg-white/60"></div>
                              <div className="h-1 w-5 bg-white/60"></div>
                              <div className="h-1 w-5 bg-white/60"></div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              })()}

            {openAccordionId !== "stripe-connect" && (
              <div
                className={cn(
                  "rounded-[20px] border border-dashed p-5 transition-colors bg-card border-card hidden lg:block",
                )}
                style={{
                  transition: "background-color 250ms ease-in-out, border-color 250ms ease-in-out",
                }}
              >
                <div>
                  <h3 className="text-[18px] leading-6.25 font-semibold text-primary mb-3.75">
                    {sectionTips.title}
                  </h3>
                </div>
                <ul className="font-montserrat text-[14px] font-normal leading-[25px] text-primary list-none">
                  {sectionTips.tips.map((tip, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span>.</span>
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div
              className={
                isEdit
                  ? "border bg-card border-card rounded-lg p-5 lg:mt-4"
                  : "md:mt-6 border bg-card border-card rounded-lg p-5"
              }
            >
              {submitError && (
                <div className="p-3 rounded-md">
                  <p className="text-sm text-brand-terracotta-bright">{submitError}</p>
                </div>
              )}

              {!isEdit && onPreview && (
                <Button
                  onClick={onPreview}
                  disabled={accordionItems.some(item => item.error)}
                  className="w-full uppercase text-sm mb-3.75"
                  variant="secondary"
                >
                  {t("createRitual.buttons.previewRitual")}
                </Button>
              )}

              {!isEdit && onSaveDraft && (
                <Button
                  onClick={() => onSaveDraft(true)}
                  className="w-full uppercase text-sm"
                  variant="outline"
                  // A draft needs at least a title (the ritual's identity); keep
                  // Save Draft disabled until one is entered.
                  disabled={isSubmitting || !titleValue?.trim()}
                >
                  {t("createRitual.buttons.saveDraft")}
                </Button>
              )}

              {isEdit && onUpdate && (
                <Button
                  onClick={async () => {
                    if (isSubmitting || accordionItems.some(item => item.error)) return
                    const isValid = await form.trigger()
                    if (isValid) {
                      await flushPendingVideoChanges()
                      await onUpdate()
                    }
                  }}
                  disabled={isSubmitting || accordionItems.some(item => item.error)}
                  className="w-full uppercase text-sm"
                  variant="secondary"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="size-4 animate-spin mr-2" />
                      {t("editRitual.updating")}
                    </>
                  ) : (
                    t("editRitual.updateRitual")
                  )}
                </Button>
              )}

              {isEdit && onNavigate && (
                <Button
                  onClick={() => onNavigate(`/rituals/${editProps?.ritual?.id}`)}
                  disabled={isSubmitting}
                  className="w-full mt-4 uppercase text-sm"
                  variant="outline"
                >
                  {t("buttons.discard")}
                </Button>
              )}

              {isEdit && onCancelRitual && !editProps?.ritual?.canceledAt && (
                <Button
                  type="button"
                  variant="outline-gradient"
                  onClick={onCancelRitual}
                  disabled={isSubmitting}
                  className="w-full uppercase text-sm mt-4 font-semibold !text-brand-terracotta bg-[#c867571a] hover:!bg-[#c867571a] hover:!text-brand-terracotta py-3.75 h-12.5"
                  style={{ border: "1px dashed rgba(200, 103, 87, 0.50)", borderRadius: "100px" }}
                >
                  {t("rituals:cancelRitual.button")}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={tipModalOpen} onOpenChange={setTipModalOpen}>
        <DialogContent className="sm:max-w-[425px] !fixed !top-[5%] !translate-y-0 rounded-[20px] border border-dashed p-5 transition-colors bg-card border-card">
          <DialogHeader>
            <DialogTitle className="text-[18px] leading-6.25 font-semibold text-primary text-start">
              {sectionTips.title}
            </DialogTitle>
          </DialogHeader>
          <ul className="font-montserrat text-[14px] font-normal leading-[25px] text-primary list-none space-y-1">
            {tipModalSectionId &&
              getRitualSectionTips(tipModalSectionId).tips.map((tip, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span>.</span>
                  <span>{tip}</span>
                </li>
              ))}
          </ul>
        </DialogContent>
      </Dialog>

      {isOrganizerModalOpen && (
        <SelectRitualOrganizersModal
          isOpen={isOrganizerModalOpen}
          onClose={() => setIsOrganizerModalOpen(false)}
          onAddOrganizers={onAddOrganizers}
          existingOrganizerIds={organizers.map(org => org.id)}
          currentUserId={user?.id}
          ritualId={isEdit ? editProps?.ritual?.id : undefined}
        />
      )}

      {renderModals?.()}
    </div>
  )
}
